<!--
 * @Author       : Qoder
 * @Date         : 2026-04-30
 * @Description  : 悬浮气泡组件（类似 van-floating-bubble）
 *
 * 使用说明：
 * - 支持拖拽移动，松手自动吸附左右边缘
 * - 支持默认插槽自定义内容
 * - 支持点击事件
 *
 * Props:
 *   - offsetX: 初始 X 偏移（默认 null，自动靠右）
 *   - offsetY: 初始 Y 偏移（默认 200）
 *   - gap: 边缘安全距离，默认 24
 *   - 固定尺寸 90x90px
 *   - draggable: 是否可拖拽，默认 true
 *   - zIndex: 层级，默认 999
 *
 * Events:
 *   - click: 点击气泡时触发
 *   - offsetChange: 位置变化时触发 (x, y)
-->
<template>
  <Teleport to="body">
    <div ref="bubbleRef" class="izt-floating-bubble" :style="bubbleStyle" @touchstart.passive="onTouchStart" @touchmove.prevent="onTouchMove" @touchend="onTouchEnd" @mousedown="onMouseDown">
      <slot>
        <div class="izt-floating-bubble__default">
          <img :src="src" alt="">
        </div>
      </slot>
    </div>
  </Teleport>
</template>

<script setup name="izt-floating-bubble">
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

defineOptions({ name: 'izt-floating-bubble' });

const props = defineProps({
  offsetX: {
    type: Number,
    default: null
  },
  offsetY: {
    type: Number,
    default: 200
  },
  gap: {
    type: Number,
    default: 8
  },

  draggable: {
    type: Boolean,
    default: true
  },
  zIndex: {
    type: Number,
    default: 999
  },
  src: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['click', 'offsetChange']);

const bubbleRef = ref(null);

// 气泡实际尺寸（从 DOM 读取，避免 CSS 与 JS 不同步）
const bubbleSize = ref({ width: 90, height: 90 });

const updateBubbleSize = () => {
  if (bubbleRef.value) {
    bubbleSize.value.width = bubbleRef.value.offsetWidth;
    bubbleSize.value.height = bubbleRef.value.offsetHeight;
  }
};

// 当前位置状态
const state = reactive({
  x: 0,
  y: 0,
  dragging: false,
  moved: false
});

// 拖拽开始时的触摸/鼠标位置与元素位置
let startInfo = {
  clientX: 0,
  clientY: 0,
  startX: 0,
  startY: 0
};

// 窗口尺寸
const winWidth = ref(window.innerWidth);
const winHeight = ref(window.innerHeight);

const updateWinSize = () => {
  winWidth.value = window.innerWidth;
  winHeight.value = window.innerHeight;
};

// 计算边界限制后的坐标
const clampPosition = (x, y) => {
  const maxX = winWidth.value - bubbleSize.value.width - props.gap;
  const maxY = winHeight.value - bubbleSize.value.height - props.gap;
  return {
    x: Math.max(props.gap, Math.min(x, maxX)),
    y: Math.max(props.gap, Math.min(y, maxY))
  };
};

// 磁吸到最近的左右边缘
const magneticToEdge = () => {
  const halfW = bubbleSize.value.width / 2;
  const centerX = state.x + halfW;
  const midScreen = winWidth.value / 2;
  const targetX = centerX < midScreen ? props.gap : winWidth.value - bubbleSize.value.width - props.gap;
  state.x = targetX;
};

// 初始化位置
const initPosition = () => {
  updateWinSize();
  const defaultX = props.offsetX !== null
    ? props.offsetX
    : winWidth.value - bubbleSize.value.width - props.gap;
  const pos = clampPosition(defaultX, props.offsetY);
  state.x = pos.x;
  state.y = pos.y;
};

// 气泡样式
const bubbleStyle = computed(() => {
  return {
    position: 'fixed',
    left: `${state.x}px`,
    top: `${state.y}px`,
    zIndex: props.zIndex,
    transition: state.dragging ? 'none' : 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    cursor: props.draggable ? 'move' : 'pointer',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none'
  };
});

// 通用拖拽开始
const handleStart = (clientX, clientY) => {
  if (!props.draggable) return;
  state.dragging = true;
  state.moved = false;
  startInfo = {
    clientX,
    clientY,
    startX: state.x,
    startY: state.y
  };
};

// 通用拖拽移动
const handleMove = (clientX, clientY) => {
  if (!state.dragging) return;
  const deltaX = clientX - startInfo.clientX;
  const deltaY = clientY - startInfo.clientY;

  if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
    state.moved = true;
  }

  const newX = startInfo.startX + deltaX;
  const newY = startInfo.startY + deltaY;
  const pos = clampPosition(newX, newY);
  state.x = pos.x;
  state.y = pos.y;

  emit('offsetChange', { x: state.x, y: state.y });
};

// 通用拖拽结束
const handleEnd = () => {
  if (!state.dragging) return;
  state.dragging = false;
  magneticToEdge();
  emit('offsetChange', { x: state.x, y: state.y });
};

// Touch 事件
const onTouchStart = (e) => {
  const touch = e.touches[0];
  handleStart(touch.clientX, touch.clientY);
};

const onTouchMove = (e) => {
  const touch = e.touches[0];
  handleMove(touch.clientX, touch.clientY);
};

const onTouchEnd = () => {
  handleEnd();
};

// Mouse 事件
const onMouseDown = (e) => {
  handleStart(e.clientX, e.clientY);

  const onMouseMove = (ev) => {
    handleMove(ev.clientX, ev.clientY);
  };

  const onMouseUp = () => {
    handleEnd();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};

// 点击事件（只有在没有拖拽移动时才触发）
const onClick = (e) => {
  if (!state.moved) {
    emit('click', e);
  }
};

// 监听 resize
const onResize = () => {
  updateWinSize();
  updateBubbleSize();
  // 重新限制位置
  const pos = clampPosition(state.x, state.y);
  state.x = pos.x;
  state.y = pos.y;
  magneticToEdge();
};

onMounted(async () => {
  await nextTick();
  updateBubbleSize();
  initPosition();
  window.addEventListener('resize', onResize);
  // 点击事件委托到元素上（touchend / click）
  if (bubbleRef.value) {
    bubbleRef.value.addEventListener('click', onClick);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  if (bubbleRef.value) {
    bubbleRef.value.removeEventListener('click', onClick);
  }
});
</script>

<style lang="scss" scoped>
.izt-floating-bubble {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.45));
  box-sizing: border-box;
  overflow: hidden;

  &:active {
    opacity: 0.85;
  }

  &__default {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}
</style>
